import React, { useState, useEffect } from "react"
import styled from "@emotion/styled"
import { css } from "@emotion/core"
import { useSelector, useDispatch } from "react-redux"
import { navigate } from "gatsby"
import { useIntl } from "react-intl"

import { PrimaryButton, SecondaryButton } from "."
import ACTIONS from "../redux/actions/actionTypes"
import { LabelStyled, InfoCard } from "."
import { setEligibility, setUserFinalConfirmation } from "../redux/actions"

const TugoPurchaseFlow = () => {
  const intl = useIntl()
  const [step, setStep] = useState(1)
  const [preStep, setPrevStep] = useState(0)
  const dispatch = useDispatch()
  const currentPath = useSelector(state => state.currentPath)

  const tugoWording = {
    firstCheck: {
      label: intl.formatMessage({ id: "tugo.firstEligibility.cap" }),
      color: "var(--clr-primary-200)",
      wording: [
        {
          content: [
            intl.formatMessage({ id: "tugo.firstEligibility1" }),
            intl.formatMessage({ id: "tugo.firstEligibility2" }),
            intl.formatMessage({ id: "tugo.firstEligibility3" }),
            // intl.formatMessage({ id: "tugo.firstEligibility4" }),
            intl.formatMessage({ id: "tugo.firstEligibility5" }),
          ],
        },
      ],
    },
    eligibility: {
      label: intl.formatMessage({ id: "tugo.eligibility.header" }),
      color: "var(--clr-primary-200)",
      wording: [
        {
          subCap: intl.formatMessage({ id: "tugo.eligibility.cap" }),
          content: [
            intl.formatMessage({ id: "tugo.eligibility.content1" }),
            intl.formatMessage({ id: "tugo.eligibility.content2" }),
            intl.formatMessage({ id: "tugo.eligibility.content3" }),
            intl.formatMessage({ id: "tugo.eligibility.content4" }),
            intl.formatMessage({ id: "tugo.eligibility.content5" }),
            intl.formatMessage({ id: "tugo.eligibility.content6" }),
            intl.formatMessage({ id: "tugo.eligibility.content7" }),
            intl.formatMessage({ id: "tugo.eligibility.content8" }),
            intl.formatMessage({ id: "tugo.eligibility.content9" }),
            intl.formatMessage({ id: "tugo.eligibility.content10" }),
          ],
        },
      ],
    },
    finalConfirmation: `<b>Important information<b> <br>This document does not constitute the entire insurance policy and you have been advised to read the policy booklet for full details on coverage and exclusions. <br><u>Applicable to all plans<u> <br>• You understand that the medical conditions disclosed on this application may not be covered and that details of when preexisting conditions are covered are set out in the policy booklet. <br>• A pre-existing condition exclusion may apply to you. Read the policy booklet for details on the pre-existing condition exclusion that may apply to you. <br>• You hereby authorize any hospital, physician or other person or organization that has records or knowledge of your health or medical history to provide that information to the company (as defined in the policy booklet) and Claims at TuGo and you authorize the company and Claims at TuGo to use and disclose that information to determine whether any claim that may be made is covered by this policy or by another plan or policy. <br>• In the event of a claim that may require or result in hospitalization, call Claims at TuGo immediately. <br><tab>From Canada & USA: 1-800-663-0399<tab> <br><tab>From Mexico: 001-800-514-9976<tab> <br><tab>Collect call worldwide: 604-278-4108<tab> <br>• You or any representative purchasing insurance on your behalf consent to TuGo and Claims at TuGo collecting, using and disclosing your personal information for the purposes of approving your insurance application, confirming coverage and/or benefits, processing your claims as well as informing you of other products and services offered by TuGo and its affiliates. You have read and agreed to TuGo’s Privacy Policy, which is available at https://www.tugo.com/en/privacy/. <br>• You may already have travel insurance through another insurance provider (e.g., a credit card or group plan). However, this may not provide adequate coverage for your trip. <br>• Coverage under this Policy will be void if you do not meet the eligibility requirements for the plan selected. Coverage under this Policy will be void if there is any misrepresentation or concealment of or failure to disclose any facts asked at the time of application. <br><u>Applicable to Emergency Medical Insurance<u> <br>• You confirm that you are not currently experiencing symptoms. <br>• You confirm that you have not seen a physician and/or other registered medical practitioner since your arrival in Canada (or on or within the last 182 days if your arrival date is more than 182 days from the application date). <br>• You understand that a waiting period may apply for any claims related to disease or illness if the policy was purchased after arrival in Canada. For complete details, refer to the policy wording. <br><u>Applicable to Trip Cancellation & Trip Interruption and Trip Interruption Only<u> <br>• You confirm that currently no circumstance is known which might reasonably be expected to prevent your travel as scheduled. <br>You have read the terms and conditions which apply to the policy as set out above and understand and agree to those terms and conditions on behalf of yourself and all persons for whom you are applying for insurance.`,
  }
  // if component unmounts and url changes, clear all fields
  useEffect(() => {
    return () => {
      if (
        currentPath !== "/get-quote-purchase" &&
        currentPath !== "/get-quote-purchase/review" &&
        currentPath !== "/get-quote-purchase/payment" &&
        currentPath !== ""
      ) {
        dispatch({
          type: ACTIONS.CLEAR_ALL_PURCHASECONFIRMATION,
        })
      }
    }
  }, [currentPath])
  const flow = {
    1: () => (
      <FirshCheck
        onProceed={() => {
          dispatch(setEligibility(true))
          setStep(step + 1)
          setPrevStep(1)
        }}
        onGoBack={() => {
          dispatch({
            type: ACTIONS.CLEAR_ELIGIBILITY,
          })
          setPrevStep(0)
          navigate("/get-quote-policies")
        }}
        tugoWording={tugoWording}
      />
    ),
    2: () => (
      <EligibilityCheck
        onProceed={() => {
          dispatch(setEligibility(true))
          setStep(step + 1)
          setPrevStep(1)
        }}
        onGoBack={() => {
          dispatch({
            type: ACTIONS.CLEAR_ELIGIBILITY,
          })
          setStep(preStep)
          setPrevStep(preStep - 1)
        }}
        tugoWording={tugoWording}
      />
    ),
    3: () => (
      <FinalConfirmation
        onProceed={() => {
          dispatch(setUserFinalConfirmation(true))
          navigate("/get-quote-purchase/review")
        }}
        onGoBack={() => {
          dispatch(setUserFinalConfirmation(null))
          setStep(preStep)
          setPrevStep(preStep - 1)
        }}
        tugoWording={tugoWording}
      />
    ),
  }
  return <Wrapper>{flow[step]()}</Wrapper>
}

export const FirshCheck = ({ onProceed, onGoBack, tugoWording }) => {
  const tugoItems = tugoWording.firstCheck
  const intl = useIntl()
  return (
    <Wrapper>
      <LabelStyled
        css={css`
          margin-bottom: 1rem;
          text-align: center;
        `}
      >
        {tugoItems.label}
      </LabelStyled>
      <InnerWrapper>
        <InfoCard
          color={tugoItems.color}
          key={tugoItems.label}
          data={tugoItems.wording}
          label=""
          fontSize="0.875rem"
          labelTextAlign="center"
          labelBtmMargin="1.25rem"
        />
      </InnerWrapper>
      <div className="btnWrapper">
        <PrimaryButton
          textSize="0.875rem"
          textTransform="capitalize"
          maxWidth="none"
          margin="0 4rem 0 0"
          onClick={onProceed}
        >
          {intl.formatMessage({ id: "btn.yestoall" })}
        </PrimaryButton>
        <SecondaryButton
          buttonColor="var(--clr-neutral-400)"
          textSize="0.875rem"
          margin="none"
          onClick={onGoBack}
        >
          {intl.formatMessage({ id: "btn.goback" })}
        </SecondaryButton>
      </div>
    </Wrapper>
  )
}

const EligibilityCheck = ({ onProceed, onGoBack, tugoWording }) => {
  const intl = useIntl()
  const tugoItems = tugoWording.eligibility
  return (
    <Wrapper>
      <InnerWrapper>
        <InfoCard
          color={tugoItems.color}
          key={tugoItems.label}
          data={tugoItems.wording}
          label={tugoItems.label}
          fontSize="0.875rem"
          labelTextAlign="center"
          labelBtmMargin="1.25rem"
        />
      </InnerWrapper>
      <div className="btnWrapper">
        <PrimaryButton
          textSize="0.875rem"
          textTransform="capitalize"
          maxWidth="none"
          margin="0 4rem 0 0"
          onClick={onProceed}
        >
          {intl.formatMessage({ id: "btn.eligible" })}
        </PrimaryButton>
        <SecondaryButton
          buttonColor="var(--clr-neutral-400)"
          textSize="0.875rem"
          margin="none"
          onClick={onGoBack}
        >
          {intl.formatMessage({ id: "btn.notEligible" })}
        </SecondaryButton>
      </div>
    </Wrapper>
  )
}

export const FinalConfirmation = ({ onProceed, onGoBack }) => {
  const strong = string => {
    if (string.includes("<b>")) {
      return string.split("<b>")[1]
    }
  }
  const underline = string => {
    if (string.includes("<u>")) {
      return string.split("<u>")[1]
    }
  }
  const tab = string => {
    if (string.includes("<tab>")) {
      return string.split("<tab>")[1]
    }
  }
  // look behind is not supported in Firefox or Safari
  // const strong = string => string.match(/(?<=<b>)(.*)(?=<b>)/g)
  // const underline = underline => underline.match(/(?<=<u>)(.*)(?=<u>)/g)
  // const tab = tab => tab.match(/(?<=<tab>)(.*)(?=<tab>)/g)
  const intl = useIntl()

  return (
    <Wrapper>
      <LabelStyled
        css={css`
          margin-bottom: 1rem;
          text-align: center;
        `}
      >
        {intl.formatMessage({ id: "tugo.terms.cap" })}
      </LabelStyled>
      <div
        css={css`
          margin-bottom: 3rem;
          line-height: 1.5;
        `}
      >
        {intl
          .formatMessage({ id: "tugo.finalConfirmation" })
          .split("<br>")
          .map((item, idx) => (
            <p key={idx}>
              {strong(item) ? (
                <b>{strong(item)}</b>
              ) : underline(item) ? (
                <u>{underline(item)}</u>
              ) : tab(item) ? (
                <span
                  css={css`
                    margin-left: 1.25rem;
                  `}
                >
                  {tab(item)}
                </span>
              ) : (
                item
              )}
            </p>
          ))}
      </div>
      <div className="btnWrapper">
        <PrimaryButton
          textSize="0.875rem"
          textTransform="capitalize"
          maxWidth="none"
          margin="0 4rem 0 0"
          onClick={onProceed}
        >
          {intl.formatMessage({ id: "btn.confirm" })}
        </PrimaryButton>
        <SecondaryButton
          buttonColor="var(--clr-neutral-400)"
          textSize="0.875rem"
          margin="none"
          onClick={onGoBack}
        >
          {intl.formatMessage({ id: "btn.goback" })}
        </SecondaryButton>
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 90%;
  margin: 0 auto;

  .btnWrapper {
    display: flex;
    justify-content: center;
    margin-top: 1rem;
  }
`

const InnerWrapper = styled.div`
  max-width: 700px;
  margin: 0 auto;
`

export default TugoPurchaseFlow
